import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'shared-ui-input',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.id]': 'null',
  },
})
export class InputComponent {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly control = input<FormControl | null>(null);
  readonly id = input.required<string>();
  readonly label = input.required<string>();
  readonly type = input<'text' | 'email' | 'tel' | 'date'>('text');
  readonly placeholder = input('');
  readonly value = input<string | null>(null);
  readonly displayValue = input<string | null>(null);
  readonly readonly = input(false);
  readonly optional = input(false);
  readonly errorText = input('');
  readonly warningText = input('');
  readonly warningOriginalValue = input<string | null>(null);
  readonly showWarning = input(false);
  readonly revertEnabled = input(false);
  readonly revertValue = input<string | null>(null);
  readonly invalid = input<boolean | null>(null);

  readonly valueChanged = output<string>();
  readonly blurred = output<void>();

  protected useFormControl(): boolean {
    return !!this.control() && this.displayValue() === null;
  }

  protected inputValue(): string {
    return this.displayValue() ?? this.value() ?? '';
  }

  protected showRevertButton(): boolean {
    return this.revertEnabled() && this.revertValue() !== null && !this.readonly();
  }

  protected inputPaddingClass(): string {
    return this.showRevertButton() ? 'pr-10' : '';
  }

  protected showInvalid(): boolean {
    if (this.readonly()) return false;

    const explicitInvalid = this.invalid();

    if (explicitInvalid !== null) return explicitInvalid;

    const control = this.control();

    return !!control && control.invalid && (control.touched || control.dirty);
  }

  protected statusText(): string {
    if (this.showInvalid()) return this.errorText();
    if (this.showWarningState()) return this.warningText();

    return '';
  }

  protected statusClass(): string {
    return this.showInvalid() ? 'text-red-500' : 'text-yellow-700';
  }

  protected showWarningState(): boolean {
    return !this.showInvalid() && this.showWarning();
  }

  protected inputClass(): string {
    if (this.showInvalid()) {
      return 'border-red-400 placeholder-gray-400 focus:border-red-400 focus:ring-1 focus:ring-red-400';
    }

    if (this.showWarningState()) {
      const focusClass = this.readonly()
        ? ''
        : ' placeholder-gray-400 focus:border-yellow-700 focus:ring-1 focus:ring-yellow-700';

      return `border-yellow-700${focusClass}${this.readonly() ? ' bg-gray-50 text-gray-700' : ''}`;
    }

    if (this.readonly()) return 'border-gray-200 bg-gray-50 text-gray-700';

    return 'border-gray-300 placeholder-gray-400 focus:border-green-800 focus:ring-1 focus:ring-green-800';
  }

  protected onValueChanged(input: HTMLInputElement): void {
    this.valueChanged.emit(input.value);
  }

  protected onRevertClicked(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const value = this.revertValue();

    if (value === null) return;

    const input = this.elementRef.nativeElement.querySelector('input');

    if (input) {
      input.value = value;
    }

    this.updateValue(value);
  }

  private updateValue(value: string): void {
    if (this.useFormControl()) {
      const control = this.control();

      control?.setValue(value);
      control?.markAsDirty();
      control?.markAsTouched();
      return;
    }

    this.valueChanged.emit(value);
  }
}
